/**
 * tone-method-fields.ts - single source of truth for the tone/method field list.
 *
 * These are the DemoState keys that:
 *   - appear in MethodPartial (eligible for bulk-region patching)
 *   - are controlled by the Tone tab and round-trip via Save/Export/Import
 *   - are present in EffectiveDialValues (flat dial-value snapshot)
 *
 * Consumers:
 *   - ui/tone-presets.ts  → PRESET_CONTROLLED_KEY_LIST / PRESET_CONTROLLED_KEYS
 *   - util/method-patch.ts → MethodPartial (Pick over this set)
 *   - state.ts            → EffectiveDialValues (requires all fields)
 */

import type { DemoState } from '../state';

/**
 * Ordered list of every tone/method field key. Typed as a readonly tuple of
 * `keyof DemoState` so callers can derive `Pick<>` or `Set<>` from it.
 */
export const TONE_METHOD_FIELDS = [
  // Family selector.
  'family',
  // Family A / C common halftone and stamp controls.
  'algorithmA',
  'cellSize',
  'stampType',
  'stampCoverage',
  'polarity',
  'density',
  'blackThreshold',
  'whiteThreshold',
  'gridAngleDeg',
  'shapeAngleDeg',
  'shapeFlipX',
  'shapeFlipY',
  'algorithmJitter',
  'grainStrength',
  'grainScale',
  // Family C algorithm controls.
  'matrixSize',
  'serpentine',
  'fsStrength',
  'atkinsonStrength',
  'brightness',
  'contrast',
  // Family B custom-pattern controls.
  'isGrayscale',
  'blurRadius',
  'patternBrightness',
  'patternContrast',
  'patternBlendMode',
  'patternScale',
  'patternRotationDeg',
  'patternFlipX',
  'patternFlipY',
  'patternNoise',
  'patternNoiseScale',
  'patternHalftoneEnabled',
  'patternHalftoneCellSize',
  'patternHalftoneStampType',
  'patternHalftoneStampCoverage',
  'patternHalftonePolarity',
  'patternHalftoneDensity',
  'patternHalftoneBlackThreshold',
  'patternHalftoneWhiteThreshold',
  'patternHalftoneGridAngleDeg',
  'patternHalftoneShapeAngleDeg',
  'patternHalftoneShapeFlipX',
  'patternHalftoneShapeFlipY',
  'patternHalftoneAlgorithmJitter',
  'patternHalftoneNoiseScale',
  'patternInvert',
  'patternOpacity',
] as const satisfies readonly (keyof DemoState)[];

/** Union of all tone/method field keys. */
export type ToneMethodField = (typeof TONE_METHOD_FIELDS)[number];
